import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { useRouter } from 'next/router'
import commerce from '@lib/api/commerce'
import { Layout } from '@components/common'
import { ProductView } from '@components/product'
import Storyblok, { StoryblokContent, useStoryblok } from '@lib/storyblok'
import { StoryData } from 'storyblok-js-client'
import { ProductContent } from 'types/storyblok'
import DiagnosticView from '@components/product/DiagnosticView'

export async function getStaticProps({
  params,
  locale,
  locales,
  preview,
}: GetStaticPropsContext<{ slug: string }>) {
  const config = { locale, locales }
  const pagesPromise = commerce.getAllPages({ config, preview })
  const siteInfoPromise = commerce.getSiteInfo({ config, preview })
  const productPromise = commerce.getProduct({
    variables: { slug: params!.slug },
    config,
    preview,
  })

  const allProductsPromise = commerce.getAllProducts({
    variables: { first: 4 },
    config,
    preview,
  })
  const { pages } = await pagesPromise
  const { categories } = await siteInfoPromise
  const { product } = await productPromise

  const sbParams = {
    version: 'draft',
    resolve_relations: ['benefits'],
  }

  const { data }: { data: { story: StoryData } } = await Storyblok.get(
    `cdn/stories/product/${params!.slug}`,
    sbParams
  )

  if (!data.story) {
    throw "Product not available in StoryBlok"
  }

  const story : StoryData = data.story
  story.content.benefits = story.content.benefits ? story.content.benefits.map((uuid: string) => {
    //@ts-ignore Not sure why rels doesn't exist on the response in the types
    return data.rels.find((rel) => { return rel.uuid == uuid })
  }) : []

  if (!product) {
    throw new Error(`Product with slug '${params!.slug}' not found`)
  }

  return {
    props: {
      pages,
      product,
      categories,
      story,
    },
    revalidate: 10,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  let { products } = await commerce.getAllProductPaths()
  products = products.filter(p => !p.path?.includes('dev-test'))

  return {
    paths: locales?.reduce<string[]>((arr, locale) => {
          // Add a product path for every locale
          products.forEach((product: any) => {
            arr.push(`/${locale}/product${product.path}`)
          })
          return arr
        }, []),
    fallback: false,
  }
}

export default function Slug({
  product,
  story,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  // @ts-ignore
  const router = useRouter()
  const content = story.content

  const hasStory = typeof story?.content !== 'undefined'

  if (router.isFallback) {
    return <h1>Loading...</h1>
  } else if (hasStory) {
    return (
      <>
        {content.category === 'diagnostic' ? (
          <DiagnosticView product={product} story={content as ProductContent} />
        ): (
          <ProductView product={product} story={content as ProductContent} />
        )}
      </>
    )
  }

  throw new Error('No CMS content available for product.')
}

Slug.Layout = Layout
